import moment from "moment";
import { DATE_RANGE_PRESETS } from "../Shared/DateRangePicker";

/** AppMenu code of the screen (Users › Access rights): Edit = reply to a feedback and set its status. */
export const MENU_CODE = "feedback";

/** HCFeedback.TypeofFeedback: what the feedback is about. */
export const FEEDBACK_TYPES = {
  1: { label: "Hotel", icon: "fas fa-hotel" },
  2: { label: "Local vendor", icon: "fas fa-car" },
  3: { label: "Hotel shuttle", icon: "fas fa-shuttle-van" },
};

/** HCFeedbackIssueStatus. Resolved and Canceled close a feedback. */
export const STATUSES = {
  1: { label: "New", tone: "new", icon: "fas fa-envelope" },
  2: { label: "In progress", tone: "progress", icon: "fas fa-spinner" },
  3: { label: "Hotel updated", tone: "updated", icon: "fas fa-hotel" },
  4: { label: "Resolved", tone: "resolved", icon: "fas fa-check-circle" },
  5: { label: "Canceled", tone: "canceled", icon: "fas fa-ban" },
};

/** Statuses staff can give with a reply (a reply always moves the feedback on from New). */
export const REPLY_STATUSES = [2, 3, 4, 5];

export const REPLY_LENGTH = 2000;

export const FILTERS = [
  { id: "all", label: "All feedback", icon: "fas fa-comment-dots", tone: "primary" },
  { id: "1", label: "Hotel", icon: FEEDBACK_TYPES[1].icon, tone: "primary" },
  { id: "3", label: "Hotel shuttle", icon: FEEDBACK_TYPES[3].icon, tone: "primary" },
  { id: "2", label: "Local vendor", icon: FEEDBACK_TYPES[2].icon, tone: "primary" },
  { id: "needsReply", label: "Needs reply", icon: "fas fa-reply", tone: "muted" },
  { id: "closed", label: "Closed", icon: "fas fa-check-circle", tone: "success" },
];

/** Date presets of the filter: the usual ones plus longer ranges (crew send feedback rarely). */
export const RANGE_PRESETS = [
  ...DATE_RANGE_PRESETS,
  { label: "Last 3 Months", getRange: () => [moment().subtract(3, "month").add(1, "day"), moment()] },
  { label: "Last 12 Months", getRange: () => [moment().subtract(12, "month").add(1, "day"), moment()] },
];

/** First range shown: the last 3 months up to today. */
export const defaultRange = (today = moment()) => [
  moment(today).subtract(3, "month").add(1, "day").startOf("day").toDate(),
  moment(today).startOf("day").toDate(),
];

export const typeLabel = (row) => FEEDBACK_TYPES[row?.feedbackType]?.label ?? "Other";
export const statusOf = (row) => STATUSES[row?.statusId] ?? STATUSES[1];
export const isClosed = (row) => row?.statusId === 4 || row?.statusId === 5;

/** Open, and either never answered (New) or the crew member wrote last. */
export const needsReply = (row) => !isClosed(row) && (row?.statusId === 1 || Boolean(row?.lastFromCrew));

export function matchesFilter(row, id) {
  switch (id) {
    case "all":
      return true;
    case "needsReply":
      return needsReply(row);
    case "closed":
      return isClosed(row);
    default:
      return String(row.feedbackType) === id;
  }
}

export const filterRows = (rows, id) => rows.filter((r) => matchesFilter(r, id));

/** Longest date range without a crew member, in days (same as the API). */
export const MAX_RANGE_DAYS = 366;

/**
 * Problem of a search before sending it, or "": a date range, a crew member, or both (same rules as the API).
 * The one-year limit applies only without a crew member.
 */
export function searchError({ range = [null, null], crew = "" } = {}) {
  const [from, to] = range;
  const hasCrew = Boolean(crew && crew.trim());
  if (!from && !to) return hasCrew ? "" : "Select a date range or a crew member.";
  if (!from || !to) return "Select both the From and To dates.";
  if (moment(to).isBefore(from, "day")) return "The To date is before the From date.";
  if (!hasCrew && moment(to).diff(moment(from), "days") >= MAX_RANGE_DAYS) return "Choose a range of one year at most, or a crew member.";
  return "";
}

/** Status the reply box starts on: the current one once moved on from New, else In progress. */
export const defaultReplyStatus = (row) => (REPLY_STATUSES.includes(row?.statusId) ? row.statusId : 2);

/** Problem of a reply before sending it, or "". */
export function replyError(text, statusId) {
  const reply = (text ?? "").trim();
  if (!REPLY_STATUSES.includes(statusId)) return "Select the status.";
  if (!reply) return "Write the reply.";
  if (reply.length > REPLY_LENGTH) return `The reply can have ${REPLY_LENGTH} characters at most.`;
  return "";
}

/** "30/07/2026 08:21" from the API's "yyyy-MM-ddTHH:mm:ss" (shown as stored); "" when empty. */
export const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "");

/** Last activity of a row: the latest reply, else when it was sent. */
export const lastActivity = (row) => row?.lastReplyAt || row?.createdAt || "";

/**
 * The filled details of a feedback, in reading order: [{ label, value }].
 * Hotel feedback has the stay; transport feedback has the trip and the pickup answers.
 */
export function detailItems(d) {
  const at = formatDateTime;
  const items = [
    ["Crew", [d.crewName, d.employeeNumber && `(${d.employeeNumber})`].filter(Boolean).join(" ")],
    ["Position", d.designation],
    ["Phone", d.phone],
    ["Email", d.email],
    ["Base station", d.baseStation],
    ["Issue", d.issueType],
    ["Flight", [d.flightNumber, d.flightType].filter(Boolean).join(" · ")],
    ["Flight time", at(d.flightTime)],
    ["Reporting time", at(d.reportingTime)],
    ["Transport time", at(d.transportTime)],
    ["Check-in", at(d.checkInDate)],
    ["Check-out", at(d.checkOutDate)],
    ["Room", d.roomNumber],
    ["Vehicle delay at pickup", d.vehicleDelay],
    ["Crew reached pickup", d.crewArrival],
    ["Vehicle reached pickup", d.vehicleArrival],
  ];
  return items.filter(([, value]) => value && String(value).trim()).map(([label, value]) => ({ label, value }));
}
